"use client";

import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
} from "../../../components/ui/card";
import { ArrowLeft, ChevronDown } from "lucide-react";
//import Link from "next/link";
import { Separator } from "../../../components/ui/separator";
import { useNavigate } from "react-router-dom";
import Header from "../../../layouts/Header";

const TermsOfService = () => {
  const navigate = useNavigate();
  const sections = [
    {
      title: "1. Introduction",
      content: `
        Welcome to our platform. By accessing or using our services, you agree to comply with these Terms of Service.
        \n\nSubheading:
        \n1. This agreement governs your use of our services.
        \n2. Please read it carefully before using our platform.
      `,
    },
    {
      title: "2. Account Requirements",
      content: `
        To use certain features of our services, you must create an account and provide accurate information. 
        \n\nSubheading:
        \n1. You are responsible for maintaining the confidentiality of your account.
        \n2. Notify us immediately of any unauthorized access to your account.
      `,
    },
    {
      title: "3. Prohibited Activities",
      content: `
        You agree not to engage in the following activities:
        \n\n- Violating any applicable laws or regulations.
        \n- Disrupting or interfering with the security of our services.
        \n- Engaging in fraudulent or deceptive practices.
      `,
    },
    {
      title: "4. Intellectual Property Rights",
      content: `
        Our platform and its contents are protected by intellectual property laws:
        \n\n- You may not copy, modify, or distribute our content without prior permission.
        \n- All rights, title, and interest in our services belong to us.
      `,
    },
    {
      title: "5. Limitation of Liability",
      content: `
        We are not responsible for:
        \n\n1. Indirect, incidental, or consequential damages.
        \n2. Any loss of data, revenue, or profits.
        \n3. Issues arising from third-party services or interruptions.
      `,
    },
    {
      title: "6. Modifications to the Terms",
      content: `
        We may update these Terms of Service at any time:
        \n\n- Updates will be posted on this page.
        \n- Continued use of our services constitutes acceptance of the revised terms.
      `,
    },
  ];

  const [selectedSection, setSelectedSection] = useState<number>(0);
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  return (
    <>
      <Header />
      <div className="min-h-screen w-full bg-gray-100 flex flex-col">
        {/* Top Gradient Bar */}
        <div className="h-14 bg-gradient-to-r from-blue-600 to-purple-600 flex items-center pl-20 text-white">
          <ArrowLeft
            className="mr-5 bg-transparent rounded-full p-1 border-gray-200/60 border hover:border-gray-400 hover:bg-white/20"
            onClick={() => {
              navigate("/settings/about");
            }}
          />
          <button
            className="text-sm font-medium underline mr-2 cursor-pointer"
            onClick={() => {
              navigate("/settings/index");
            }}
          >
            Settings
          </button>
          <p className="text-sm font-medium"> / About / Terms of Service</p>
        </div>

        {/* Main Content */}
        <div className="flex justify-center mt-8 px-4 md:px-16 lg:px-32">
          <div className="max-w-5xl w-full">
            <Card className="bg-white rounded-2xl shadow-sm">
              <CardHeader className="px-6 pt-6 pb-4">
                <CardTitle className="text-lg font-semibold text-gray-500">
                  Terms of Service
                </CardTitle>
                <p className="text-sm text-gray-400 mt-2">
                  Last updated: 13 August 2024
                </p>
              </CardHeader>

              <CardContent className="px-6 pb-6">
                {/* Dropdown Section */}
                <div className="relative mb-6">
                  <button
                    className="w-full px-4 py-3 border border-gray-200 rounded-md flex justify-between items-center text-gray-800 hover:bg-gray-50"
                    onClick={() => setDropdownOpen(!isDropdownOpen)}
                  >
                    <span className="text-sm font-medium">
                      {sections[selectedSection].title}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 w-full bg-white border border-gray-200 rounded-md mt-2 shadow-lg z-10">
                      {sections.map((section, index) => (
                        <button
                          key={index}
                          className={`w-full px-4 py-3 text-left text-sm text-gray-800 hover:bg-gray-50 ${
                            selectedSection === index
                              ? "border-l-4 border-rose-500"
                              : ""
                          }`}
                          onClick={() => {
                            setSelectedSection(index);
                            setDropdownOpen(false);
                          }}
                        >
                          {section.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Section Content */}
                <div>
                  <h2 className="text-md font-bold text-gray-800">
                    {sections[selectedSection].title}
                  </h2>
                  <p className="text-sm text-gray-600 whitespace-pre-line">
                    {sections[selectedSection].content}
                  </p>
                </div>

                <Separator className="mt-10" />

                {/* Footer */}
                <div className="flex justify-center mt-8">
                  <p className="text-sm text-gray-500">
                    Questions?{" "}
                    <p
                      className="font-bold text-rose-500 hover:underline cursor-pointer"
                      onClick={() => {
                        navigate("/support/index");
                      }}
                    >
                      Contact us
                    </p>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;
